import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '../lib/utils';

type ToastKind = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  /** Show a toast. Returns nothing — fire-and-forget from event handlers. */
  toast: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const ICON = { success: CheckCircle2, error: XCircle, info: Info } as const;
const ACCENT = {
  success: 'text-emerald-500',
  error: 'text-destructive',
  info: 'text-primary',
} as const;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const toast = useCallback(
    (message: string, kind: ToastKind = 'info') => {
      const id = nextId.current++;
      setToasts((t) => [...t, { id, kind, message }]);
      // Errors linger longer than confirmations.
      window.setTimeout(() => dismiss(id), kind === 'error' ? 6000 : 3500);
    },
    [dismiss],
  );

  const success = useCallback((message: string) => toast(message, 'success'), [toast]);
  const error = useCallback((message: string) => toast(message, 'error'), [toast]);

  return (
    <ToastContext.Provider value={{ toast, success, error }}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end">
        {toasts.map((t) => {
          const Icon = ICON[t.kind];
          return (
            <div
              key={t.id}
              role="status"
              className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm shadow-lg animate-in slide-in-from-bottom-2 fade-in"
            >
              <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', ACCENT[t.kind])} />
              <span className="flex-1 text-card-foreground">{t.message}</span>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                className="rounded p-0.5 text-muted-foreground hover:text-foreground"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

const NOOP: ToastContextValue = { toast: () => {}, success: () => {}, error: () => {} };

// Falls back to no-ops outside a provider: toasts are a non-critical enhancement,
// so a component rendered in isolation (e.g. a unit test) must not crash for the
// want of a ToastProvider.
export function useToast(): ToastContextValue {
  return useContext(ToastContext) ?? NOOP;
}
